import { useState } from 'react';
import { AlertTriangle, Shield, ShieldOff, Trash, Skull } from 'lucide-react';
import type { UserDetail } from '../UserDetailPage';
import { SoftDeleteModal } from './SoftDeleteModal';
import { HardDeleteModal } from './HardDeleteModal';

// API helper with cookie auth
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

interface Props {
  user: UserDetail;
  onRefresh: () => void;
}

export function UserDangerZoneTab({ user, onRefresh }: Props) {
  const [showSoftDelete, setShowSoftDelete] = useState(false);
  const [showHardDelete, setShowHardDelete] = useState(false);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [suspendError, setSuspendError] = useState<string | null>(null);

  const handleSuspend = async () => {
    setSuspendLoading(true);
    setSuspendError(null);
    try {
      if (user.active) {
        const reason = prompt('Suspension reason:');
        if (!reason) return;
        await fetchApi(`/api/v1/admin/users/${user.id}/suspend`, {
          method: 'POST',
          body: JSON.stringify({ reason }),
        });
      } else {
        await fetchApi(`/api/v1/admin/users/${user.id}/unsuspend`, {
          method: 'POST',
          body: JSON.stringify({ notes: 'Unsuspended via admin panel' }),
        });
      }
      onRefresh();
    } catch (err) {
      setSuspendError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSuspendLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-6 w-6 text-red-400" />
        <h2 className="text-lg font-semibold text-red-400">Danger Zone</h2>
      </div>

      <div className="bg-red-900/10 border border-red-800/30 rounded-lg p-4">
        <p className="text-sm text-red-300">
          Actions in this section are potentially destructive. Please review carefully before proceeding.
        </p>
      </div>

      {suspendError && (
        <div className="px-4 py-2 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">{suspendError}</div>
      )}

      {/* Suspend / Unsuspend */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              {user.active ? <ShieldOff className="h-4 w-4 text-amber-400" /> : <Shield className="h-4 w-4 text-green-400" />}
              {user.active ? 'Suspend User' : 'Unsuspend User'}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              {user.active
                ? 'Temporarily disable this user\'s access to the platform. Can be reversed.'
                : 'Restore this user\'s access to the platform.'}
            </p>
            {user.suspendReason && (
              <p className="text-xs text-amber-400 mt-2">
                Suspended for: {user.suspendReason}
              </p>
            )}
          </div>
          <button
            onClick={handleSuspend}
            disabled={suspendLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
              user.active
                ? 'bg-amber-600 hover:bg-amber-500 text-white'
                : 'bg-green-600 hover:bg-green-500 text-white'
            }`}
          >
            {suspendLoading ? 'Processing...' : user.active ? 'Suspend' : 'Unsuspend'}
          </button>
        </div>
      </div>

      {/* Soft Delete */}
      <div className="bg-slate-800/50 border border-red-800/30 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Trash className="h-4 w-4 text-red-400" />
              Soft Delete (TARK Compliance)
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Deactivate the account and anonymize all personal data. The database record is preserved for audit purposes.
              Use this for policy violations, fraud, or spam accounts.
            </p>
          </div>
          <button
            onClick={() => setShowSoftDelete(true)}
            className="px-4 py-2 text-sm font-medium bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Soft Delete
          </button>
        </div>
      </div>

      {/* Hard Delete */}
      <div className="bg-slate-800/50 border-2 border-red-700/50 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Skull className="h-4 w-4 text-red-500" />
              Hard Delete (User Deletion Request)
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Permanently delete the user and ALL associated data from the database.
              This is <strong className="text-red-400">irreversible</strong>. Use only when a user has requested complete data deletion.
            </p>
            <p className="text-xs text-red-400 mt-2">
              This removes MySQL data and executes best-effort Cognito/S3/DynamoDB cleanup with a detailed result report.
            </p>
          </div>
          <button
            onClick={() => setShowHardDelete(true)}
            className="px-4 py-2 text-sm font-medium bg-red-800 hover:bg-red-700 text-white rounded-lg transition-colors border border-red-600"
          >
            Hard Delete
          </button>
        </div>
      </div>

      {/* Modals */}
      {showSoftDelete && (
        <SoftDeleteModal
          user={user}
          onClose={() => setShowSoftDelete(false)}
          onSuccess={() => {
            setShowSoftDelete(false);
            onRefresh();
          }}
        />
      )}

      {showHardDelete && (
        <HardDeleteModal
          user={user}
          onClose={() => setShowHardDelete(false)}
          onSuccess={() => {
            setShowHardDelete(false);
            // Navigate back to list since user no longer exists
            window.location.href = '/users';
          }}
        />
      )}
    </div>
  );
}
